import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavBar, SideNav, Breadcrumb } from "../../components";
import { Button, Checkbox } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  fetchSlots,
  createFreeSlots,
  deleteSlots,
} from "../../redux/slices/calendar";
import { errorHandler } from "../../helpers/handlers";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const dispatch = useDispatch();
  const { slots, actionLoading } = useSelector((state) => state.calendar);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [singleDeletingSlot, setSingleDeletingSlot] = useState(null);

  // Fetch slots from the backend
  useEffect(() => {
    dispatch(fetchSlots());
  }, [dispatch]);

  // Transform fetched slots into calendar events
  useEffect(() => {
    if (slots) {
      const transformedEvents = Object.keys(slots).flatMap((dateKey) =>
        slots[dateKey].map((slot) => {
          const start = new Date(slot.startTime);
          const end = new Date(slot.endTime);

          return {
            id: slot.id,
            title: slot.title || "Available Slot", // Use application title if assigned
            start,
            end,
            isAvailable: !slot.application, // Add a flag to indicate availability
          };
        })
      );

      setEvents(transformedEvents);
    }
  }, [slots]);

  // Handle day selection
  const handleSelectSlot = (slotInfo) => {
    setSelectedDay(slotInfo.start);
  };

  const handleGenerateSlots = async () => {
    if (!selectedDay) {
      errorHandler({ message: "Please select a day first!" });
      return;
    }

    // Check if the selected day is today or earlier
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate comparison
    if (selectedDay.getTime() <= today.getTime()) {
      errorHandler({
        message:
          "You cannot create slots for today or earlier. Please select a future date.",
      });
      return;
    }

    // Format the selected day as a local date string (YYYY-MM-DD)
    const date = format(selectedDay, "yyyy-MM-dd"); // Use date-fns to format the date

    try {
      // Dispatch the createFreeSlots thunk
      await dispatch(createFreeSlots({ date }));
    } catch (error) {
      errorHandler({ message: "Failed to create free slots." });
    }
  };

  const toggleSlotSelection = (slotId) => {
    setSelectedSlots(
      (prevSelected) =>
        prevSelected.includes(slotId)
          ? prevSelected.filter((id) => id !== slotId) // Deselect if already selected
          : [...prevSelected, slotId] // Add to selection
    );
  };

  const handleDeleteSelectedSlots = async () => {
    if (selectedSlots.length === 0) {
      errorHandler({ message: "No slots selected for deletion." });
      return;
    }

    try {
      await dispatch(deleteSlots(selectedSlots));
      setSelectedSlots([]); // Clear selection after deletion
    } catch (error) {
      errorHandler({ message: "Failed to delete selected slots." });
    }
  };

  const handleDeleteSingleSlot = async (slots) => {
    try {
      setSingleDeletingSlot(slots[0]);
      await dispatch(deleteSlots(slots));
      setSingleDeletingSlot(null); // Clear selection after deletion
    } catch (error) {
      errorHandler({ message: "Failed to delete selected slots." });
    }
  };

  // Customize event styles
  const eventPropGetter = (event) => {
    if (!event.isAvailable) {
      return {
        style: {
          backgroundColor: "#FF6B6B", // Red for unavailable slots
          color: "white",
        },
      };
    }
    return {
      style: {
        backgroundColor: "#4CAF50", // Green for available slots
        color: "white",
      },
    };
  };

  return (
    <>
      <div className="antialiased bg-gray-50 dark:bg-gray-900">
        <NavBar />
        <SideNav />
        <main className="p-4 md:ml-64 h-auto pt-20">
          <div className="sticky top-14 z-20 bg-gray-50 dark:bg-gray-900">
            <Breadcrumb />
          </div>
          <div className="mt-4">
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 500 }}
              selectable
              onSelectSlot={handleSelectSlot}
              eventPropGetter={eventPropGetter} // Apply custom styles
            />
            {selectedDay && (
              <div className="mt-4 p-4 bg-white shadow-md rounded-lg">
                <h2 className="text-xl font-semibold">
                  Events for {selectedDay.toDateString()}
                </h2>
                <ul>
                  {events
                    .filter(
                      (event) =>
                        event.start.toDateString() ===
                        selectedDay.toDateString()
                    )
                    .sort((a, b) => a.start - b.start) // Sort events by start time
                    .map((event, index) => (
                      <li
                        key={index}
                        className="text-gray-700 flex justify-between items-center border-b py-2"
                      >
                        <div className="flex items-center">
                          <Checkbox
                            id="remember"
                            color="purple"
                            className="mr-2"
                            checked={selectedSlots.includes(event.id)}
                            onChange={() => toggleSlotSelection(event.id)}
                            disabled={actionLoading || !event.isAvailable}
                          />
                          <span>
                            {event.title} ({event.start.toLocaleTimeString()} -{" "}
                            {event.end.toLocaleTimeString()})
                          </span>
                        </div>
                        <Button
                          size="sm"
                          color="red"
                          onClick={() => handleDeleteSingleSlot([event.id])}
                          disabled={actionLoading || !event.isAvailable}
                          isProcessing={
                            singleDeletingSlot
                              ? singleDeletingSlot === event.id && actionLoading
                              : selectedSlots.includes(event.id) &&
                                actionLoading
                          }
                          processingSpinner={
                            <AiOutlineLoading className="h-6 w-6 animate-spin" />
                          }
                        >
                          Delete
                        </Button>
                      </li>
                    ))}
                </ul>
                {selectedSlots.length > 0 && (
                  <Button
                    size="sm"
                    color="purple"
                    className="mt-4"
                    onClick={handleDeleteSelectedSlots}
                    disabled={actionLoading}
                    isProcessing={actionLoading}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                  >
                    Delete Selected Slots ({selectedSlots.length})
                  </Button>
                )}
                {selectedDay > new Date() &&
                  !events.some(
                    (event) =>
                      event.start.toDateString() ===
                        selectedDay.toDateString() &&
                      event.title === "Available Slot"
                  ) && (
                    <Button
                      size="sm"
                      color="purple"
                      onClick={handleGenerateSlots}
                      disabled={actionLoading}
                      isProcessing={actionLoading}
                      processingSpinner={
                        <AiOutlineLoading className="h-6 w-6 animate-spin" />
                      }
                    >
                      Generate Bookable Slots
                    </Button>
                  )}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Calendar;
